import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, IconButton, Menu, MenuItem } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "../components/Box";
import PromoCodeModal from "../components/PromoCodeModal";
import SortableTable from "../components/SortableTable";
import Title from "../components/Title";
import { API_URL } from "../constants/config";
import { useUser } from "../contexts/UserContext";
import DashboardLayout from "../layouts/Dashboard";
import { errorAlert } from "../utils/toast";

const PromoCodeManagement = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  useEffect(() => {
    if (user?.role !== "administrator") {
      navigate("/dashboard");
    }
  }, [navigate, user?.role]);

  const fetchPromoCodes = () => {
    setLoading(true);
    let token = localStorage.getItem("token");
    axios
      .get(`${API_URL}/promo-codes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log("response", response);
        setPromoCodes(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        setPromoCodes([]);
        setLoading(false);
        errorAlert("Failed to fetch promo codes.");
      });
  };

  const handleMenuClick = (event, promoCode) => {
    setAnchorEl(event.currentTarget);
    setSelectedPromoCode(promoCode);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const fields = [
    { id: "code", label: "Promo Code" },
    { id: "discount_type", label: "Discount Type" },
    { id: "discount_value", label: "Discount Value" },
    { id: "expiration_date", label: "Expiration Date" },
    { id: "usage_limit", label: "Usage Limit" },
    { id: "min_report_count", label: "Min Report Count" }, // Updated field name and label
    { id: "max_report_count", label: "Max Report Count" }, // Updated field name and label
    { id: "status", label: "Status" },
    { id: "actions", label: "Actions" },
  ];

  const formatCell = (row, value, column) => {
    if (column === "actions") {
      return (
        <IconButton onClick={(event) => handleMenuClick(event, row)}>
          <MoreVertIcon />
        </IconButton>
      );
    }

    if (column === "status") {
      const status = row.status;
      let statusColor = "default";
      let statusText = "Unknown";

      if (status === "active") {
        statusColor = "success";
        statusText = "Active";
      } else if (status === "inactive") {
        statusColor = "error";
        statusText = "Inactive";
      }

      return <Chip label={statusText} color={statusColor} size="small" />;
    }

    return value;
  };

  const toggleModal = (mode) => {
    if (mode === "add") {
      setIsEditMode(false);
      setSelectedPromoCode(null);
    } else if (mode === "Edit") {
      setIsEditMode(true);
      setSelectedPromoCode(selectedPromoCode);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleEditPromoCode = () => {
    setIsEditMode(true);
    toggleModal("Edit");
  };

  return (
    <DashboardLayout>
      {loading && (
        <div className="relative z-10">
          <div className="preloader-main w-full h-screen bg-white absolute opacity-90">
            <div className="preloader-div flex items-center justify-center flex-col h-full">
              Loading...
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center ">
        <Title title="Promo Code Management" />
        <button
          className="block px-4 py-1 text-sm border border-cf--primary text-cf--primary bg-white min-w-[90px] rounded-sm transition-all hover:text-white pt-sans-bold hover:bg-cf--primary mb-4"
          onClick={() => toggleModal("add")}
        >
          Add Promo Code
        </button>
      </div>
      <Box>
        <div className="w-full">
          <SortableTable
            data={promoCodes}
            fields={fields}
            formatCells={formatCell}
          />
        </div>
      </Box>

      <PromoCodeModal
        open={isModalOpen}
        handleClose={toggleModal}
        isEditMode={isEditMode}
        selectedPromoCode={selectedPromoCode}
        refreshPromoCodes={fetchPromoCodes}
        setIsEditMode={setIsEditMode}
        setSelectedPromoCode={setSelectedPromoCode}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => navigate(`/promo-code-usage/${selectedPromoCode.id}`)}
        >
          <ListItemIcon>
            <VisibilityIcon />
          </ListItemIcon>
          Usages
        </MenuItem>

        <MenuItem onClick={handleEditPromoCode}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Edit
        </MenuItem>
      </Menu>
    </DashboardLayout>
  );
};

export default PromoCodeManagement;
